import { getSupabaseClient } from "../../providers/supabase/supabase";
import { mxAt, mxDaysFromToday, mxYmd } from "../mxDate";

// Valores de contact_notes.outcome (RAFTEL.md §Resultados)
export const OUTCOMES = [
  { value: "sin_respuesta", label: "Sin respuesta" },
  { value: "buzon", label: "Buzón" },
  { value: "contesto_otro", label: "Contestó otra persona" },
  { value: "hablo_decisor", label: "Hablé con decisor" },
  { value: "respondio", label: "Respondió" },
  { value: "demo_agendada", label: "Demo agendada" },
  { value: "no_interesado", label: "No interesado" },
  { value: "baja", label: "Pidió baja" },
  { value: "numero_incorrecto", label: "Número incorrecto" },
  { value: "rebote", label: "Rebote" },
];
// Estos cierran el seguimiento: no se sugiere siguiente paso
export const CLOSING = ["no_interesado", "baja", "numero_incorrecto", "rebote"];

export const NEXT = [
  { days: 1, label: "Mañana" },
  { days: 3, label: "+3 días" },
  { days: 7, label: "+1 semana" },
  { days: 0, label: "Sin siguiente" },
];

// contact_notes.commitment (T.5): compromiso obtenido en el toque
export const COMMITMENTS = [
  { value: "tiempo", label: "Tiempo (llamada agendada)" },
  { value: "presentacion", label: "Presentación con quien decide" },
  { value: "dinero", label: "Dinero (carta, anticipo, piloto pagado)" },
];

// a media mañana, hora de México
const inDays = (days: number) => mxAt(mxDaysFromToday(days), "10:00");

export type LogInput = {
  contactId: number;
  dealId: number | null;
  channel: string; // llamada | whatsapp | email | nota
  outcome: string;
  note: string;
  nextDays: number; // 0 = sin siguiente paso
  nextTaskType: string; // tipo de la tarea siguiente (call, whatsapp…)
  nextTaskText: string;
  /** tareas a cerrar: una en Mi día; en la sesión, las pendientes de hoy de la oportunidad */
  close: { taskId: number } | { dealTasksDueToday: true } | "none";
  contactUpdate?: Record<string, unknown>;
  /** para "Demo agendada": etapa actual y etapas del producto */
  deal?: { stage: string; stages: string[] };
  /** Bloque L: duración de la llamada, ya calculada (null si no aplica) */
  durationSeconds?: number | null;
  /** Bloque T.5: compromiso obtenido (null/ninguno = no sube el termómetro) */
  commitment?: string | null;
  /** Bloque T.4: variante de plantilla usada en este toque, para medir qué vende */
  templateId?: number | null;
  /** workspace explícito de la nota y la tarea; sin él cae al default de la BD (default_workspace_id()) */
  workspaceId?: number | null;
};

const endOfToday = () =>
  new Date(mxAt(mxDaysFromToday(1)).getTime() - 1).toISOString();

/**
 * Registra una actividad (PRD C.2): cierra la(s) tarea(s), crea la nota con
 * canal, resultado y oportunidad, y la tarea del siguiente paso. "Demo agendada"
 * mueve la oportunidad a Demo (si su producto tiene esa etapa y no va más adelante).
 * "Baja" y "Número incorrecto" alimentan No contactar por el trigger de la BD.
 */
export const logActivity = async (i: LogInput) => {
  const sb = getSupabaseClient();
  const now = new Date().toISOString();
  const label = OUTCOMES.find((o) => o.value === i.outcome)?.label ?? i.outcome;
  const check = ({ error }: { error: { message: string } | null }) => {
    if (error) throw new Error(`No se pudo guardar: ${error.message}`);
  };

  // 1. Cerrar tareas primero: si el resultado detiene la cadencia, el trigger
  //    borra las pendientes y éstas ya no serían "pendientes".
  if (i.close === "none") {
    // desde la ficha: solo registrar
  } else if ("taskId" in i.close) {
    check(
      await sb
        .from("tasks")
        .update({ done_date: now })
        .eq("id", i.close.taskId),
    );
  } else if (i.dealId) {
    check(
      await sb
        .from("tasks")
        .update({ done_date: now })
        .eq("deal_id", i.dealId)
        .is("done_date", null)
        .lte("due_date", endOfToday()),
    );
  }
  // 2. La actividad
  check(
    await sb.from("contact_notes").insert({
      contact_id: i.contactId,
      deal_id: i.dealId,
      channel: i.channel === "otro" ? "nota" : i.channel,
      outcome: i.outcome || null,
      text: i.note.trim() || label,
      date: now,
      duration_seconds: i.durationSeconds ?? null,
      commitment: i.commitment ?? null,
      template_id: i.templateId ?? null,
      ...(i.workspaceId ? { workspace_id: i.workspaceId } : {}),
    }),
  );
  check(
    await sb
      .from("contacts")
      .update({ last_seen: now, ...i.contactUpdate })
      .eq("id", i.contactId),
  );
  // 3. Demo agendada → etapa Demo (nunca hacia atrás). Sin etapa "demo", la de
  //    reunión (la tercera), igual que public.demo_stage() para Cal.com.
  const dealUpdate: Record<string, unknown> = {};
  if (i.outcome === "demo_agendada" && i.deal) {
    const stage = i.deal.stages.includes("demo") ? "demo" : i.deal.stages[2];
    const demo = i.deal.stages.indexOf(stage);
    if (demo >= 0 && i.deal.stages.indexOf(i.deal.stage) < demo)
      dealUpdate.stage = stage;
  }
  // 4. Siguiente paso: tarea con fecha y "próximo paso" de la oportunidad
  if (i.nextDays > 0) {
    const due = inDays(i.nextDays);
    check(
      await sb.from("tasks").insert({
        contact_id: i.contactId,
        deal_id: i.dealId,
        type: i.nextTaskType,
        text: i.nextTaskText,
        due_date: due.toISOString(),
        ...(i.workspaceId ? { workspace_id: i.workspaceId } : {}),
      }),
    );
    dealUpdate.next_step_at = mxYmd(due);
  }
  if (i.dealId && Object.keys(dealUpdate).length) {
    check(
      await sb
        .from("deals")
        .update({ ...dealUpdate, updated_at: now })
        .eq("id", i.dealId),
    );
  }
};

export type DictationResult = {
  ai?: boolean; // lo organizó la IA
  note: string;
  outcome?: string;
  nextDays?: number;
};

/**
 * Convierte lo dictado en registro (PRD C.3): la IA elegida extrae resultado, nota y
 * siguiente paso; Ian revisa antes de guardar. Si la IA no responde, queda solo la nota.
 */
export const interpretDictation = async (
  text: string,
): Promise<DictationResult> => {
  const { data, error } = await getSupabaseClient().functions.invoke("ai", {
    body: { action: "dictate", text },
  });
  if (error || !data?.note) return { note: text };
  return {
    note: data.note,
    outcome: data.outcome ?? undefined,
    nextDays: data.nextDays ?? undefined,
    ai: true,
  };
};
